import { useTranslation } from "@opendash/i18n";
import { useWatch } from "antd/es/form/Form";
import { useMemo } from "react";
import { Operation, ParseField } from "../types/FormValues";
import { OPERATIONS } from "../types/Operations";
import { useMappedParseFields } from "./useMappedParseFields";

/**
 * Computes the field and operation options for the current operation
 * @param index the operation index in the form list
 * @returns the field and operation options for the current operation
 */
export const useOperationOptions = (index: number) => {
  const t = useTranslation();
  const fields = useMappedParseFields().filter(
    (field) => field.field.indexOf(".") === -1 // Queries do not support nested fields
  ); // filter out nested fields
  const operationsWatched = useWatch<Operation[]>(["operations"]);

  // getting the selected field, to see wich operations are allowed
  const currentOperation = operationsWatched
    ? operationsWatched[index]
    : undefined;
  const selectedField = currentOperation?.field?.value;

  // options for the operation select depending on the selected field type
  const operationOptions = useMemo(() => {
    const selectedFieldObject = fields.find(
      (field) => field.field === selectedField
    );
    if (!selectedField || !selectedFieldObject) return [];

    // return the operations that are allowed for the selected field type
    return OPERATIONS.filter((op) =>
      op.types.some((type) => type === selectedFieldObject?.type)
    ).map((op) => {
      return {
        label: t("openware:parse.form.operations.label." + op.key),
        value: op.key,
      };
    });
  }, [selectedField, fields]);

  // options for the field select mapped from the selected field
  const fieldOptions: ParseField[] = useMemo(() => {
    return fields.map((field) => {
      return {
        label: field.field + " (" + field.type + ")",
        value: field.field,
        type: field.type,
        targetClass: field.targetClass,
      };
    });
  }, [fields, selectedField]);
  return { fieldOptions, operationOptions, currentOperation };
};
